import React from "react";
import { FormControlLabel, Link, Typography } from "@mui/material";
import { Checkbox, Disclosure, IconButton, toast } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Edit02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Trans, useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { OnboardingInfo } from "pages/onboarding/OnboardCompany";
import { ACTIVATE_COMPANY } from "../../graphql/company";
import { useNavigate, useParams } from "react-router-dom";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { FormikAddress } from "utils/addressFormat";
import { useBrandingContext } from "context/BrandingContext";
import { fullName } from "utils/fullName";
import { formatCashFull } from "utils/formatCash";
import { User } from "hooks/useAddUpdateUser";
import {
  beneficialOwnersFor,
  profilesToSubmit,
} from "utils/onboarding/onboardingProfiles";
import { Inline, Stack } from "components/layout/reusableLayouts";
import Summary, { SummaryItem } from "components/common/Summary";
import StepHeader from "./StepHeader";
import StepFooter from "./StepFooter";

// Page indices of the wizard, which `OnboardCompany` addresses screens by.
const BUSINESS_PAGE = 0;
const AUTHORIZATION_PAGE = 1;
const OWNERS_PAGE = 2;

type ReviewProps = {
  onboardingInfo: OnboardingInfo | null;
  ownersRequired: boolean;
  handlePageSelect: (step: number) => void;
};

const formatAddress = (addr?: FormikAddress | null): string => {
  if (!addr) return "";
  const { address1, address2, city, state, zipcode } = addr;
  return [address1, address2, city && `${city},`, state, zipcode]
    .filter(Boolean)
    .join(" ");
};

const linkFor = (url?: string | null) =>
  url ? (
    <Link href={url} target="_blank" rel="noopener noreferrer" />
  ) : (
    <span />
  );

const ReviewAndSubmit: React.FC<ReviewProps> = (props) => {
  const { onboardingInfo, ownersRequired, handlePageSelect } = props;
  const { t } = useTranslation();
  const { platform } = useBrandingContext();
  const navigate = useNavigate();
  const { connectionId } = useParams();
  const { user } = useGetLoggedInUser();
  const { companyProfile, addressProfile, userProfiles } = onboardingInfo || {};
  const [activatingCompany, setActivatingCompany] = React.useState(false);
  const [esignConsented, setEsignConsented] = React.useState(false);

  const [activateCompany] = useMutation(ACTIVATE_COMPANY, {
    onCompleted: () => {
      toast.success(t("Request Accepted"));
      toast.success(t("Processing Application"));
      navigate(
        connectionId ? `/pending?connectionId=${connectionId}` : "/pending",
      );
    },
    onError: (error) => {
      setActivatingCompany(false);
      toast.error(error.message);
    },
  });

  const profiles = userProfiles ?? [];
  const authorizedPerson = profiles.find(
    (profile) => profile.email === user?.email,
  );
  const beneficialOwners = beneficialOwnersFor(profiles, ownersRequired);
  const submittedProfiles = profilesToSubmit(
    authorizedPerson,
    beneficialOwners,
  );

  function handleSubmit() {
    if (!esignConsented) {
      toast.error(t("AgreeBeforeSubmitting"));
      return;
    }
    setActivatingCompany(true);
    activateCompany({
      variables: {
        id: user?.companyId,
        ...(connectionId && { connectionId }),
        input: {
          acceptedTerms: esignConsented,
          authorizedUsers: submittedProfiles.map((profile) => ({
            id: profile.id,
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
            identifyingId: profile.identifyingId,
            identifyingType: profile.identifyingType,
            issuingCountry: profile.issuingCountry,
          })),
        },
      },
    });
  }

  const renderSection = (
    title: string,
    page: number,
    children: React.ReactNode,
  ) => (
    <Stack gap="var(--space-3)">
      <Inline justify="between" align="center" wrap={false}>
        <Typography variant="h6" component="h2">
          {title}
        </Typography>
        <IconButton
          variant="ghost"
          size="small"
          aria-label={`${t("Edit")} ${title}`}
          onClick={() => handlePageSelect(page)}
        >
          <HugeiconsIcon icon={Edit02Icon} size={20} color="currentColor" />
        </IconButton>
      </Inline>
      {children}
    </Stack>
  );

  const personItems = (person: User | undefined): SummaryItem[] => [
    { title: "Name", value: fullName(person) },
    { title: "Phone", value: person?.phone },
    { title: "Date of birth", value: person?.birthdate },
  ];

  return (
    <>
      <StepHeader
        title={t("ReviewAndSubmit")}
        subtitle={t("ReviewAndSubmitDescription")}
      />

      <Stack gap="var(--space-7)" sx={{ marginTop: "var(--space-7)" }}>
        {renderSection(
          t("Business"),
          BUSINESS_PAGE,
          <Summary
            disableContainer
            items={[
              { title: "Legal name", value: companyProfile?.businessLegalName },
              { title: "DBA", value: companyProfile?.businessDBA },
              { title: "EIN", value: companyProfile?.businessEIN },
              {
                title: "Annual revenue",
                value: formatCashFull(Number(companyProfile?.annualRevenue)),
              },
              {
                title: "Address",
                value: formatAddress(addressProfile),
                fullWidth: true,
              },
            ]}
          />,
        )}

        {renderSection(
          t("Primary Authorized Person"),
          AUTHORIZATION_PAGE,
          <Summary disableContainer items={personItems(authorizedPerson)} />,
        )}

        {beneficialOwners.length > 0 &&
          renderSection(
            t("Beneficial owners"),
            OWNERS_PAGE,
            <Stack gap="var(--space-5)">
              {beneficialOwners.map((owner, index) => (
                <Summary
                  key={owner.id ?? index}
                  disableContainer
                  items={[
                    ...personItems(owner),
                    {
                      title: "Ownership percentage",
                      value: t("OwnershipPercentageValue", {
                        value: owner.ownershipPercentage,
                      }),
                    },
                  ]}
                />
              ))}
            </Stack>,
          )}

        <Stack gap="var(--space-3)">
          {platform?.issuingBankDisclosure && (
            <Disclosure
              text={platform.issuingBankDisclosure}
              align="left"
              sx={{ paddingInline: 0 }}
            />
          )}

          <FormControlLabel
            sx={{ alignItems: "flex-start" }}
            control={
              <Checkbox
                name="esignConsented"
                checked={esignConsented}
                onChange={(e) => setEsignConsented(e.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                <Trans
                  i18nKey="ReviewAgreementConsent"
                  components={{
                    platformAgreement: linkFor(platform?.termsUrl),
                    termsConditions: linkFor(platform?.termsUrl),
                    privacyPolicy: linkFor(platform?.privacyUrl),
                    cardProgramTerms: linkFor(platform?.cardholderAgreementUrl),
                  }}
                />
              </Typography>
            }
          />
        </Stack>
      </Stack>

      <StepFooter
        primaryLabel={t("Agree and Submit")}
        onPrimary={handleSubmit}
        primaryDisabled={!esignConsented}
        primaryLoading={activatingCompany}
        secondaryLabel={t("Previous")}
        onSecondary={() =>
          handlePageSelect(ownersRequired ? OWNERS_PAGE : AUTHORIZATION_PAGE)
        }
      />
    </>
  );
};

export default ReviewAndSubmit;
